import { useState, useEffect, useContext } from "react";
import { useNavigate, Outlet } from "react-router-dom";
import { Icon } from "@iconify/react";
import AuthContext from "../../contexts/AuthContext";
import { useTheme } from "../../design-system/contexts/ThemeContext";
import { Avatar } from "../../design-system";
import { BottomNavbar } from "../../components/BottomNavbar";
import MenuActionsModal from "../../components/MenuActionsModal/MenuActionsModal";
import ProfileModal from "../../components/ProfileModal";
import ChromeWarningBanner from "../../components/ChromeWarningBanner";
import SecondaryNav from "../../components/SecondaryNav";
import DesktopHeader from "../../components/DesktopHeader";
import { events } from "../../common/db";
import { listSponsorTeam, downloadLeads } from "../../common/api";
import { shouldShowTeamsFeature } from "../../common/functions";
import { useToast } from "../../design-system/contexts/ToastContext";
// iOS keyboard is now handled via CSS dvh units and viewport meta tag
import "./TeamMembers.css";


const TeamMembers = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  // Detect iPad specifically (even in landscape/desktop mode)
  const isIPad = /iPad/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // isDesktop should be FALSE on iPad, even in landscape mode
  // iPad should use mobile UI (hamburger menu, bottom nav, etc.)
  const [isDesktop, setIsDesktop] = useState(() => {
    return window.innerWidth >= 992 && !isIPad;
  });
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventCreatedAt, setEventCreatedAt] = useState(null);

  const navigate = useNavigate();
  const Context = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const { success, error: showError } = useToast();

  // Parse URL for event context
  const fetch_url = window.location.href;
  const fetch_url_search = window.location.search;
  const urlParams = new URLSearchParams(fetch_url_search);
  const sponsor_code =
    urlParams.get("sponsor") || localStorage.getItem("sponsorCode");

  // Extract event_id from URL (handle both /events/ and /Events/)
  const eventsIndex = fetch_url.toLowerCase().indexOf("events/");
  const event_id = eventsIndex !== -1
    ? fetch_url.substring(
        eventsIndex + 7,
        fetch_url.lastIndexOf("/team")
      )
    : "";

  useEffect(() => {
    const handleResize = () => {
      // iPad should always use mobile UI regardless of screen width
      if (isIPad) {
        setIsDesktop(false);
        return;
      }
      setIsDesktop(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isIPad]);

  useEffect(() => {
    fetchEventDetails();
    if (event_id) {
      fetchTeamMembers();
    }
  }, [event_id]);

  const fetchEventDetails = async () => {
    let fetchEventDetailsFromDB = await events
      .where("eventId")
      .equals(event_id)
      .toArray();
    setEventName(fetchEventDetailsFromDB[0]?.eventName || "");
    setEventDate(fetchEventDetailsFromDB[0]?.startDate || "");
    setEventCreatedAt(fetchEventDetailsFromDB[0]?.createdAt || null);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    if (value) {
      const filtered = teamMembers.filter(
        (member) =>
          member.name.toLowerCase().includes(value.toLowerCase()) ||
          member.email.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredMembers(filtered);
    } else {
      setFilteredMembers(teamMembers);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      if (!event_id || !sponsor_code) {
        console.warn("Event ID or Sponsor Code is missing, cannot fetch team members");
        setIsLoading(false);
        return;
      }

      const response = await listSponsorTeam(event_id, sponsor_code);
      const teamData = response?.data?.listSponsorTeam?.items || [];

      // Map API response to UI format
      // API returns: emailId, imageUrl, leadsCount, name, userId
      const teamMembersList = teamData.map((member) => ({
        id: member.userId || member.emailId,
        name: member.name || member.userId || member.emailId?.split("@")[0] || "Unknown",
        email: member.emailId || "",
        avatar: member.imageUrl || null,
        leadsCount: member.leadsCount || 0,
      }));

      setTeamMembers(teamMembersList);

      // Update filtered members based on current search
      if (searchValue) {
        const filtered = teamMembersList.filter(
          (member) =>
            member.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
            member.email?.toLowerCase().includes(searchValue.toLowerCase())
        );
        setFilteredMembers(filtered);
      } else {
        setFilteredMembers(teamMembersList);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      setTeamMembers([]);
      setFilteredMembers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);

    try {
      await fetchTeamMembers();
    } catch (error) {
      console.error("Error refreshing team members:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const onClickHome = () => navigate({ pathname: "/events" });
  const onClickScan = () =>
    navigate({
      pathname: `/events/${event_id}/scanDetails`,
      search: `?sponsor=${sponsor_code}`,
    });
  const onClickLeads = () =>
    navigate({
      pathname: `/events/${event_id}/view-leads`,
      search: `?sponsor=${sponsor_code}`,
    });
  const onClickAddEvent = () => navigate({ pathname: "/addEventModal" });
  
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const res = await downloadLeads(sponsor_code, event_id);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in downloadLeads:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to download leads";
        showError(errorMessage);
        setIsDownloading(false);
        return;
      }

      let allLeads = JSON.parse(res.data.downloadSponsorLeads);
      if (allLeads.statusCode === 200) {
        setIsDownloading(false);
        success("Downloading file");
        window.location.assign(allLeads.body.downloadUrl);
      } else {
        setIsDownloading(false);
        const errorMsg = allLeads.body || "Download failed";
        showError(errorMsg);
      }
    } catch (err) {
      console.error("Error in downloadLeads:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to download leads";
      showError(errorMessage);
      setIsDownloading(false);
    }
  };

  const shareClickHandler = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    if (!event_id) {
      console.error("Event ID is missing, cannot navigate to share page", { event_id, fetch_url });
      return;
    }
    
    const sharePath = `/events/${event_id}/team/ShareSponsorCode`;
    const shareSearch = sponsor_code ? `?sponsor=${sponsor_code}` : '';
    
    navigate({ 
      pathname: sharePath,
      search: shareSearch
    });
  };

  // ===== Format Date =====
  const formatEventDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const totalMembers = teamMembers.length;

  return (
    <div className="team-page">
      {/* Chrome Warning Banner (Desktop Only) */}
      <ChromeWarningBanner />

      {/* Mobile Header - Show on mobile or iPad (even in desktop/landscape mode) */}
      <header className={`team-header ${isIPad ? 'team-header--ipad' : ''}`}>
        <div className="team-header__left">
          <button className="team-header__menu-btn" onClick={() => setMenuModalOpen(true)}>
            <Icon icon="solar:hamburger-menu-broken" width="24" height="24" />
          </button>
          <h1 className="team-header__event-name">{eventName || "Event"}</h1>
        </div>
        <div className="team-header__right">
          <button className="team-header__theme-btn" onClick={toggleTheme}>
            <Icon
              icon={
                theme === "dark"
                  ? "flowbite:sun-outline"
                  : "flowbite:moon-outline"
              }
              width="24"
              height="24"
            />
          </button>
          <Avatar
            src={Context?.state?.user?.attributes?.picture}
            name={Context?.state?.user?.attributes?.name}
            size={36}
            onClick={() => setProfileModalOpen(true)}
            className="team-header__avatar"
          />
        </div>
      </header>

      {/* Desktop Header */}
      <DesktopHeader
        eventName={eventName}
        eventDate={eventDate}
        onShareClick={shareClickHandler}
        onHomeClick={onClickHome}
        onAddEventClick={onClickAddEvent}
        onProfileClick={() => setProfileModalOpen(true)}
        isDesktop={isDesktop}
      />

      {/* Desktop Secondary Nav */}
      <SecondaryNav
        activeTab="team"
        onScanClick={onClickScan}
        onLeadsClick={onClickLeads}
        onTeamClick={() => {}}
        isDesktop={isDesktop}
        showTeam={shouldShowTeamsFeature(eventCreatedAt)}
      />

      {/* Content */}
      <div className="team-page-card">
        <div className="team-content">
        {/* Team Count */}
        <div className="team-count">
          <span className="team-count__label">Team Members</span>
          <span className="team-count__badge">{totalMembers}</span>
          <button
            className={`team-count__refresh ${isRefreshing ? "team-count__refresh--spinning" : ""}`}
            onClick={handleRefresh}
            disabled={false}
          >
            <Icon icon="mynaui:refresh" width="20" height="20" />
          </button>
        </div>

        {/* Search */}
        <div className="team-search">
          <div className="team-search__input-wrapper">
            <Icon
              icon="proicons:search"
              width="18"
              height="18"
              className="team-search__icon"
            />
            <input
              type="text"
              className="team-search__input"
              placeholder="Search"
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="team-search__share-btn"
            onClick={shareClickHandler}
            aria-label="Share"
          >
            <Icon icon="mynaui:share" width="20" height="20" />
          </button>
        </div>

        {/* Team List */}
        <div className="team-list">
          {isLoading ? (
            <div className="team-list__loading">
              <Icon icon="svg-spinners:ring-resize" width="32" height="32" />
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="team-list__empty">No team members found</div>
          ) : isDesktop ? (
            // Desktop Table View
            <table className="team-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Leads Captured</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member, index) => (
                  <tr key={member.id}>
                    <td>{member.name || "-"}</td>
                    <td>{member.email || "-"}</td>
                    <td>{member.leadsCount || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            // Mobile Card View
            filteredMembers.map((member, index) => (
              <div key={member.id} className="team-card">
                <div className="team-card__main">
                  <div className="team-card__avatar">
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.name} />
                    ) : (
                      <Icon
                        icon="mdi:account"
                        width="24"
                        height="24"
                        className="team-card__avatar-placeholder"
                      />
                    )}
                  </div>
                  <div className="team-card__info">
                    <h3 className="team-card__name">{member.name}</h3>
                    <p className="team-card__email">{member.email}</p>
                  </div>
                </div>
                <div className="team-card__leads">
                  <span className="team-card__leads-badge">
                    Leads Captured:
                    <span className="team-card__leads-count">
                      {member.leadsCount}
                    </span>
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
        </div>
      </div>

      {/* Bottom Navbar - Always render, CSS controls visibility for desktop/iPad */}
      <BottomNavbar eventId={event_id} sponsorCode={sponsor_code} />

      {/* Outlet for modals */}
      <Outlet
        context={{
          duplicateEntry: null,
          eventId: event_id,
        }}
      />

      {/* Menu Actions Modal */}
      <MenuActionsModal
        isOpen={menuModalOpen}
        onClose={() => setMenuModalOpen(false)}
        eventId={event_id}
        sponsorCode={sponsor_code}
        showTeam={shouldShowTeamsFeature(eventCreatedAt)}
        onDownload={handleDownload}
      />

      {/* Profile Modal */}
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />
    </div>
  );
};

export default TeamMembers;
